import { observer } from 'mobx-react';
import { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Save, Send, ArrowLeft, Plus, Search, X, Loader2 } from 'lucide-react';
import { OOC_REASONS, OOCForecastAdjustment, OOCForecastCell } from '../oocUpdated.type';
import { OOCProvFacilityRow } from 'Interfaces/FDPInterfaces/oocInterfaces';
import {
  useOOCCreateEditLogic,
  TAB_CONFIG,
  WORKFLOW_STEPS,
  getFcColorClass,
  getStatusBadgeClass,
  getStatusLabel,
  periodToLabel,
} from './oocCreateEdit.logic';
import classes from './oocCreateEdit.module.scss';
import { useStore } from 'hooks/useStore';

const REASON_OPTIONS = OOC_REASONS.map((r) => ({ label: r, value: r }));

const WorkflowProgressBar = ({ currentStep }: { currentStep: number }) => (
  <div className={classes.workflowBar}>
    {WORKFLOW_STEPS.map((ws, idx) => {
      const isCompleted = ws.step < currentStep;
      const isActive = ws.step === currentStep;
      const circleClass = isCompleted
        ? classes.workflowCircleCompleted
        : isActive
        ? classes.workflowCircleActive
        : classes.workflowCircleInactive;
      const labelClass = isCompleted
        ? classes.workflowStepLabelCompleted
        : isActive
        ? classes.workflowStepLabelActive
        : '';
      return (
        <div key={ws.step} style={{ display: 'flex', alignItems: 'flex-start' }}>
          {idx > 0 && (
            <div className={`${classes.workflowConnector} ${isCompleted ? classes.workflowConnectorCompleted : ''}`} />
          )}
          <div className={classes.workflowStep}>
            <div className={`${classes.workflowCircle} ${circleClass}`}>
              {isCompleted ? '✓' : ws.step}
            </div>
            <span className={`${classes.workflowStepLabel} ${labelClass}`}>{ws.label}</span>
          </div>
        </div>
      );
    })}
  </div>
);

const FCColorLegend = () => (
  <div className={classes.fcLegendBar}>
    <span className={classes.fcLegendLabel}>FC Color Legend:</span>
    {[
      { color: '#d4edda', label: 'New' },
      { color: '#ffe0b2', label: 'Modified' },
      { color: '#ffcdd2', label: 'Removed' },
      { color: '#ffffff', label: 'Unchanged', bordered: true },
    ].map(({ color, label, bordered }) => (
      <span key={label} className={classes.fcLegendItem}>
        <span
          className={classes.fcLegendSwatch}
          style={{ backgroundColor: color, ...(bordered ? { border: '1px solid #ccc' } : {}) }}
        />
        {label}
      </span>
    ))}
  </div>
);

const StatusBadge = ({ status }: { status: string }) => (
  <span className={`${classes.statusBadge} ${getStatusBadgeClass(status, classes)}`}>
    {getStatusLabel(status)}
  </span>
);

const ForecastCell = observer(
  ({
    cell,
    adjustmentId,
    periodIndex,
    totalPeriods,
    onCopyForward,
  }: {
    cell: OOCForecastCell;
    adjustmentId: string;
    periodIndex: number;
    totalPeriods: number;
    onCopyForward: (id: string, fromIdx: number) => void;
  }) => {
    const { OOCStoreUpdated: OOCStore } = useStore();
    const [isFocused, setIsFocused] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/,/g, '').trim();
      const parsed = raw === '' ? null : Number(raw);
      if (raw !== '' && isNaN(parsed as number)) return;
      OOCStore.updateForecastValue(adjustmentId, periodIndex, parsed);
    };

    const displayValue =
      cell.value !== null && cell.value !== undefined ? cell.value.toLocaleString() : '';
    const showCopyDot = isFocused && periodIndex < totalPeriods - 1 && cell.value !== null;

    return (
      <td className={classes.colFc}>
        <div className={classes.fcCellWrap}>
          {cell.previousValue !== null && cell.colorType === 'modified' && (
            <span className={classes.fcPreviousValue}>was: {cell.previousValue.toLocaleString()}</span>
          )}
          <input
            className={`${classes.fcInput} ${getFcColorClass(cell.colorType, classes)}`}
            type="text"
            value={displayValue}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 150)}
            placeholder="-"
          />
          {showCopyDot && (
            <span
              className={classes.copyForwardDot}
              title="Copy to all following months"
              onMouseDown={(e) => {
                e.preventDefault();
                onCopyForward(adjustmentId, periodIndex);
              }}
            >
              .
            </span>
          )}
        </div>
      </td>
    );
  }
);

const AdjustmentRow = observer(
  ({ adjustment }: { adjustment: OOCForecastAdjustment }) => {
    const { OOCStoreUpdated: OOCStore } = useStore();
    const [isRemoving, setIsRemoving] = useState(false);
    const isLoadingBaseline = OOCStore.loadingBaselineNDCs.includes(adjustment.ndc11);
    const isMissingReason = OOCStore.missingReasonIds.includes(adjustment.id);

    const handleRemove = async () => {
      setIsRemoving(true);
      await OOCStore.removeAdjustment(adjustment.id);
      setIsRemoving(false); // only reached when removal failed — success unmounts this row
    };

    const handleCopyForward = (adjustmentId: string, fromIndex: number) => {
      const sourceValue = adjustment.forecasts[fromIndex]?.value;
      if (sourceValue === null || sourceValue === undefined) return;
      for (let i = fromIndex + 1; i < adjustment.forecasts.length; i++) {
        OOCStore.updateForecastValue(adjustmentId, i, sourceValue);
      }
    };

    return (
      <tr>
        <td className={classes.colNdc10}>{adjustment.ndc10}</td>
        <td className={classes.colNdc11}>{adjustment.ndc11}</td>
        <td className={classes.colSupplier}>{adjustment.supplier}</td>
        <td className={classes.colMolecule}>{adjustment.molecule}</td>
        <td className={classes.colConcept}>{adjustment.concept}</td>
        <td className={classes.colPackOut}>{adjustment.packOut}</td>
        <td className={`${classes.colReason} ${isMissingReason ? classes.reasonCellError : ''}`}>
          <Dropdown
            className={`${classes.reasonDropdown} ${isMissingReason ? 'p-invalid' : ''}`}
            options={REASON_OPTIONS}
            value={adjustment.reason ?? null}
            onChange={(e) => OOCStore.updateAdjustmentReason(adjustment.id, e.value)}
            placeholder="Select reason"
            filter
            filterPlaceholder="Search reason..."
          />
          {isMissingReason && <span className={classes.reasonErrorMsg}>Required</span>}
        </td>
        {isLoadingBaseline
          ? adjustment.forecasts.map((cell) => (
              <td key={cell.period} className={classes.colFc}>
                <div className={classes.skeletonCell} />
              </td>
            ))
          : adjustment.forecasts.map((cell, pIdx) => (
              <ForecastCell
                key={cell.period}
                cell={cell}
                adjustmentId={adjustment.id}
                periodIndex={pIdx}
                totalPeriods={adjustment.forecasts.length}
                onCopyForward={handleCopyForward}
              />
            ))}
        <td className={classes.colNotes}>
          <input
            className={classes.notesInput}
            type="text"
            value={adjustment.notes}
            onChange={(e) => OOCStore.updateNotes(adjustment.id, e.target.value)}
            placeholder="Add notes..."
          />
        </td>
        <td className={classes.colRemove}>
          <button className={classes.removeBtn} onClick={handleRemove} disabled={isRemoving} title="Remove NDC">
            {isRemoving ? <Loader2 size={14} className={classes.removeBtnSpinner} /> : <X size={16} />}
          </button>
        </td>
      </tr>
    );
  }
);

const ReferenceDataPanel = ({ adjustments }: { adjustments: OOCForecastAdjustment[] }) => {
  const withRef = adjustments.filter((a) => a.referenceData);
  if (withRef.length === 0) return null;

  return (
    <div className={classes.referenceDataSection}>
      <h4 className={classes.referenceDataSectionTitle}>FC Reference Data</h4>
      <div className={classes.referenceDataGrid}>
        {withRef.map((adj) => {
          const ref = adj.referenceData!;
          const isNegative = ref.percentChange.startsWith('-');
          const isPositive = ref.percentChange.startsWith('+');
          const changeClass = isNegative ? classes.negativeChange : isPositive ? classes.positiveChange : '';
          return (
            <div key={adj.id} className={classes.referenceDataBox}>
              <div className={classes.referenceDataTitle}>FC Reference Data ({ref.ndc11})</div>
              {[
                { label: '12-mo SO Avg', value: ref.twelveMonthSOAvg.toLocaleString() },
                { label: '6-mo SO Avg', value: ref.sixMonthSOAvg.toLocaleString() },
                { label: 'Current Month', value: ref.currentMonthPerformance.toLocaleString() },
                { label: 'Market Demand', value: ref.overallMarketDemand.toLocaleString() },
                { label: 'New FC', value: ref.newFC.toLocaleString() },
              ].map(({ label, value }) => (
                <div key={label} className={classes.referenceRow}>
                  <span className={classes.referenceLabel}>{label}</span>
                  <span className={classes.referenceValue}>{value}</span>
                </div>
              ))}
              <div className={classes.referenceRow}>
                <span className={classes.referenceLabel}>% Change</span>
                <span className={`${classes.referenceValue} ${changeClass}`}>{ref.percentChange}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const COL = { facility: 162, annualVol: 108, pctOfMkt: 82, month: 90 } as const;
const STICKY_LEFT = { facility: 0, annualVol: COL.facility, pctOfMkt: COL.facility + COL.annualVol } as const;
const LAST_STICKY: React.CSSProperties = {
  borderRight: '2px solid var(--border-primary)',
  boxShadow: '3px 0 6px -2px rgba(0,0,0,0.18)',
};
const stickyTh = (left: number, isLast = false): React.CSSProperties => ({
  position: 'sticky', left, top: 0, zIndex: 20, background: 'var(--bg-th-market-strategy)',
  ...(isLast ? LAST_STICKY : {}),
});
const stickyData = (left: number, bg: string, isLast = false): React.CSSProperties => ({
  position: 'sticky', left, zIndex: 10, background: bg,
  ...(isLast ? LAST_STICKY : {}),
});

const ProvMASNDCGrid = observer(
  ({ adj, periods }: { adj: OOCForecastAdjustment; periods: string[] }) => {
    const { OOCStoreUpdated: OOCStore } = useStore();
    const packNum = parseInt(adj.packOut, 10) || 1;
    const isLoading = OOCStore.loadingProvForecastNDCs.includes(adj.ndc11);
    const data = OOCStore.provForecastData[adj.ndc11];

    if (isLoading) {
      return (
        <div className={classes.provGridLoading}>
          <Loader2 size={18} className={classes.provGridSpinner} />
          <span>Loading facility parameters…</span>
        </div>
      );
    }

    if (!data || data.facilityRows.length === 0) {
      return (
        <div className={classes.provGridEmpty}>
          No market sales data found for this NDC. Provisional parameters cannot be pre-populated.
        </div>
      );
    }

    const rows = data.facilityRows;
    const cmopIdxs = rows.map((r, i) => r.cot === 'CMOP' ? i : -1).filter(i => i >= 0);
    const otherIdxs = rows.map((r, i) => r.cot !== 'CMOP' ? i : -1).filter(i => i >= 0);
    const selectedCMOPCount = rows.filter(r => r.cot === 'CMOP' && r.isSelected).length;

    const annualEUs = rows.map(r => r.isSelected ? Math.round(r.annualVolSU / packNum) : 0);
    const totalSU = rows.filter(r => r.isSelected).reduce((s, r) => s + r.annualVolSU, 0);
    const totalEU = annualEUs.reduce((s, v) => s + v, 0);
    const cmopAnnualSU = cmopIdxs.filter(i => rows[i].isSelected).reduce((s, i) => s + rows[i].annualVolSU, 0);
    const cmopAnnualEU = cmopIdxs.reduce((s, i) => s + annualEUs[i], 0);
    const cmopPctOfMkt = totalSU > 0 ? ((cmopAnnualSU / totalSU) * 100).toFixed(1) : '0.0';

    const cellEU = (row: OOCProvFacilityRow, mIdx: number): number => {
      if (!row.isSelected) return 0;
      const pct = row.monthlyPercentages[mIdx] ?? 0;
      return Math.round((row.annualVolSU / 12) * (pct / 100) / packNum);
    };

    const cmopTotals = periods.map((_, mi) => cmopIdxs.reduce((s, i) => s + cellEU(rows[i], mi), 0));
    const grandTotals = periods.map((_, mi) => rows.reduce((s, r) => s + cellEU(r, mi), 0));

    const subtotalBg = 'var(--bg-th-market-strategy)';
    const tableWidth = COL.facility + COL.annualVol + COL.pctOfMkt + COL.month * periods.length;

    const renderDataRow = (rIdx: number) => {
      const row = rows[rIdx];
      const annualEU = annualEUs[rIdx];
      const pctOfMkt = totalSU > 0 && row.isSelected
        ? ((row.annualVolSU / totalSU) * 100).toFixed(1)
        : '0.0';
      const cellBg = !row.isSelected ? '#f3f4f6' : 'var(--bg-card)';

      return (
        <tr key={rIdx} style={{ opacity: row.isSelected ? 1 : 0.6 }}>
          <td style={{ ...stickyData(STICKY_LEFT.facility, cellBg), padding: '5px 8px', border: '1px solid var(--border-primary)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Checkbox
                checked={row.isSelected}
                onChange={() => OOCStore.toggleProvFacility(adj.ndc11, rIdx)}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: '12px', lineHeight: '1.25' }}>
                  {row.facilityName}
                  {row.isContingency && (
                    <span style={{ marginLeft: 5, fontSize: '9px', fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: '#fef3c7', color: '#92400e' }}>
                      CONT
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.1' }}>
                  {row.rampGroup || row.cot}
                </div>
              </div>
            </div>
          </td>
          <td style={{ ...stickyData(STICKY_LEFT.annualVol, cellBg), padding: '5px 8px', border: '1px solid var(--border-primary)', textAlign: 'right', fontWeight: 600, fontSize: '12px' }}>
            {annualEU > 0 ? annualEU.toLocaleString() : '–'}
          </td>
          <td style={{ ...stickyData(STICKY_LEFT.pctOfMkt, cellBg, true), padding: '5px 8px', border: '1px solid var(--border-primary)', textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>
            {row.isSelected && row.annualVolSU > 0 ? `${pctOfMkt}%` : '–'}
          </td>
          {periods.map((_, mIdx) => {
            const pct = row.monthlyPercentages[mIdx] ?? 0;
            const eu = cellEU(row, mIdx);
            const isEditable = mIdx <= 3;
            const inputBg = !row.isSelected ? '#e5e7eb' : pct === 0 ? '#fef2f2' : pct === 100 ? '#f0fdf4' : undefined;
            return (
              <td key={mIdx} style={{ padding: '4px 5px', border: '1px solid var(--border-primary)', verticalAlign: 'top', background: !isEditable && row.isSelected ? '#f8f9fa' : undefined }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'flex-end' }}>
                    {isEditable ? (
                      <InputText
                        value={String(pct)}
                        disabled={!row.isSelected}
                        style={{ width: '46px', textAlign: 'right', padding: '1px 4px', fontSize: '12px', height: '24px', backgroundColor: inputBg }}
                        onChange={e => OOCStore.updateProvFacilityPct(adj.ndc11, rIdx, mIdx, parseInt(e.target.value) || 0)}
                      />
                    ) : (
                      <span style={{ display: 'inline-block', minWidth: '46px', textAlign: 'right', padding: '1px 4px', fontSize: '12px', height: '24px', lineHeight: '22px', borderRadius: '4px', background: inputBg, fontWeight: 600 }}>
                        {pct}
                      </span>
                    )}
                    <span style={{ fontSize: '11px', color: '#374151', flexShrink: 0, lineHeight: '24px' }}>%</span>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '10px', color: eu > 0 ? '#374151' : '#9ca3af', paddingRight: '15px', lineHeight: '1.2' }}>
                    {eu > 0 ? eu.toLocaleString() : '–'}
                  </div>
                </div>
              </td>
            );
          })}
        </tr>
      );
    };

    return (
      <div className={classes.provCmopWrapper}>
        {selectedCMOPCount > 0 && selectedCMOPCount <= 2 && (
          <div className={classes.provExceptionBanner}>
            Exception: {selectedCMOPCount} CMOP{selectedCMOPCount > 1 ? 's' : ''} purchasing → 100% planned
          </div>
        )}
        <div className={classes.provCmopTableWrapper}>
          <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '12px', width: `${tableWidth}px` }}>
            <colgroup>
              <col style={{ width: `${COL.facility}px` }} />
              <col style={{ width: `${COL.annualVol}px` }} />
              <col style={{ width: `${COL.pctOfMkt}px` }} />
              {periods.map((_, i) => <col key={i} style={{ width: `${COL.month}px` }} />)}
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...stickyTh(STICKY_LEFT.facility), padding: '6px 8px', textAlign: 'left', border: '1px solid var(--border-primary)', fontSize: '12px' }}>
                  CMOP / COT Facility
                </th>
                <th style={{ ...stickyTh(STICKY_LEFT.annualVol), padding: '6px 8px', textAlign: 'right', border: '1px solid var(--border-primary)', fontSize: '11px' }}>
                  Annual Vol (EUs)
                </th>
                <th style={{ ...stickyTh(STICKY_LEFT.pctOfMkt, true), padding: '6px 8px', textAlign: 'right', border: '1px solid var(--border-primary)', fontSize: '11px' }}>
                  % of Mkt
                </th>
                {periods.map((p, mi) => (
                  <th key={p} style={{ position: 'sticky', top: 0, zIndex: 3, background: mi <= 3 ? '#f0f4ff' : 'var(--bg-th-market-strategy)', padding: '6px 4px', textAlign: 'center', border: '1px solid var(--border-primary)', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    {periodToLabel(p)}
                    {mi > 3 && <div style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 400 }}>read-only</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cmopIdxs.map(renderDataRow)}
              {cmopIdxs.length > 0 && (
                <tr style={{ background: subtotalBg, fontWeight: 600 }}>
                  <td style={{ ...stickyData(STICKY_LEFT.facility, subtotalBg), padding: '5px 8px', border: '1px solid var(--border-primary)', fontSize: '12px', fontStyle: 'italic' }}>CMOP Total</td>
                  <td style={{ ...stickyData(STICKY_LEFT.annualVol, subtotalBg), padding: '5px 8px', border: '1px solid var(--border-primary)', textAlign: 'right', fontSize: '12px' }}>
                    {cmopAnnualEU > 0 ? cmopAnnualEU.toLocaleString() : '–'}
                  </td>
                  <td style={{ ...stickyData(STICKY_LEFT.pctOfMkt, subtotalBg, true), padding: '5px 8px', border: '1px solid var(--border-primary)', textAlign: 'right', fontSize: '12px', color: '#6b7280' }}>
                    {cmopAnnualSU > 0 ? `${cmopPctOfMkt}%` : '–'}
                  </td>
                  {cmopTotals.map((v, mi) => (
                    <td key={mi} style={{ padding: '5px', border: '1px solid var(--border-primary)', textAlign: 'right', fontSize: '11px' }}>
                      {v > 0 ? v.toLocaleString() : '–'}
                    </td>
                  ))}
                </tr>
              )}
              {otherIdxs.map(renderDataRow)}
              <tr style={{ background: subtotalBg, fontWeight: 700 }}>
                <td style={{ ...stickyData(STICKY_LEFT.facility, subtotalBg), padding: '6px 8px', border: '1px solid var(--border-primary)', fontSize: '12px' }}>TOTAL</td>
                <td style={{ ...stickyData(STICKY_LEFT.annualVol, subtotalBg), padding: '6px 8px', border: '1px solid var(--border-primary)', textAlign: 'right', fontSize: '12px' }}>
                  {totalEU > 0 ? totalEU.toLocaleString() : '–'}
                </td>
                <td style={{ ...stickyData(STICKY_LEFT.pctOfMkt, subtotalBg, true), padding: '6px 8px', border: '1px solid var(--border-primary)', textAlign: 'right', fontSize: '12px' }}>100%</td>
                {grandTotals.map((v, mi) => (
                  <td key={mi} style={{ padding: '6px 5px', border: '1px solid var(--border-primary)', textAlign: 'right', fontSize: '12px' }}>
                    {v > 0 ? v.toLocaleString() : '–'}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ margin: '0.3rem 0 0.4rem', fontSize: '0.71rem', color: '#6b7280' }}>
          EU = annual SU ÷ pack size ({adj.packOut}). M1–M4 editable; M5–M15 auto-propagate from M4 (CMOP) or M1 (MTF/VISN).
        </p>
      </div>
    );
  }
);

const OOCCreateEdit = () => {
  const {
    OOCStore,
    periods,
    submittedBy,
    planningPeriod,
    moleculeGroups,
    showSearchResults,
    selectedNDCs,
    handleSearchChange,
    handleSearchKeyDown,
    handleToggleNDCCheckbox,
    handleToggleMoleculeCheckbox,
    isMoleculeFullyChecked,
    isMoleculeIndeterminate,
    isAddingNDC,
    handleAddNDCButtonClick,
    handleSearchBlur,
    handleSearchFocus,
  } = useOOCCreateEditLogic();

  const { currentSubmission, activeTab, activeTabAdjustments, tabCounts, ndcSearchQuery, ndcSearchResults, isNDCSearching, isSaving } = OOCStore;

  if (!currentSubmission) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>No submission loaded.</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <WorkflowProgressBar currentStep={1} />

      <div className={classes.sectionHeader}>
        <h2 className={classes.sectionTitle}>New OOC Submission - {currentSubmission.id}</h2>
        <StatusBadge status={currentSubmission.status} />
      </div>

      <div className={classes.metaFieldsRow}>
        {[
          { label: 'Submission Date', value: currentSubmission.submissionDate },
          { label: 'Submitted By', value: submittedBy },
          { label: 'Planning Period', value: planningPeriod },
        ].map(({ label, value }) => (
          <div key={label} className={classes.formGroup}>
            <label className={classes.formLabel}>{label}</label>
            <InputText className={`${classes.formInput} ${classes.readonlyInput}`} value={value} readOnly />
          </div>
        ))}
      </div>

      <div className={`${classes.callout} ${classes.calloutInfo}`}>
        <span className={classes.calloutIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </span>
        <span>
          <strong>Ramping File Integration:</strong> NDCs from the approved ramping file are automatically pre-populated below. You can adjust forecast values, add new NDCs, assign a reason per NDC, and remove entries as needed before communicating to Supply Chain.
        </span>
      </div>

      <div className={classes.ndcSearchRow}>
        <div className={classes.ndcSearchInputWrap}>
          <Search size={16} className={classes.ndcSearchIcon} />
          <InputText
            className={classes.ndcSearchInput}
            value={ndcSearchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by NDC, molecule, or concept to add..."
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
          />
          {showSearchResults && ndcSearchResults.length > 0 && (
            <div className={classes.ndcSearchResults}>
              {Object.entries(moleculeGroups).map(([molecule, ndcs]) => (
                <div key={`group-${molecule}`}>
                  {ndcs.length > 1 && (
                    <div
                      className={`${classes.ndcSearchResultItem} ${classes.ndcGroupHeader}`}
                      onMouseDown={(e) => { e.preventDefault(); handleToggleMoleculeCheckbox(molecule); }}
                    >
                      <div className={classes.ndcResultCheckboxRow}>
                        <input
                          type="checkbox"
                          className={classes.ndcCheckbox}
                          checked={isMoleculeFullyChecked(molecule)}
                          ref={(el) => { if (el) el.indeterminate = isMoleculeIndeterminate(molecule); }}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className={classes.ndcResultInfo}>
                          <span className={classes.ndcResultNdc}>{molecule}</span>
                          <span className={classes.ndcResultMeta}>{ndcs.map((n) => n.ndc11).join(', ')}</span>
                        </div>
                      </div>
                      <span className={`${classes.ndcResultTab} ${classes.ndcGroupTag}`}>{ndcs.length} NDCs</span>
                    </div>
                  )}
                  {ndcs.map((ndc) => (
                    <div
                      key={ndc.ndc11}
                      className={`${classes.ndcSearchResultItem} ${ndcs.length > 1 ? classes.ndcChildItem : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); handleToggleNDCCheckbox(ndc.ndc11); }}
                    >
                      <div className={classes.ndcResultCheckboxRow}>
                        <input
                          type="checkbox"
                          className={classes.ndcCheckbox}
                          checked={selectedNDCs.has(ndc.ndc11)}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className={classes.ndcResultInfo}>
                          <span className={classes.ndcResultNdc}>{ndc.ndc11}</span>
                          <span className={classes.ndcResultMeta}>{ndc.supplier} | {ndc.molecule} | {ndc.concept}</span>
                        </div>
                      </div>
                      <span className={classes.ndcResultTab}>
                        → {TAB_CONFIG.find((t) => t.key === ndc.suggestedTab)?.label ?? ndc.suggestedTab}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {isNDCSearching && !showSearchResults && (
            <div className={classes.ndcSearchResults}>
              <div className={classes.ndcSearchResultItem}>
                <span className={classes.ndcResultMeta}>Searching...</span>
              </div>
            </div>
          )}
        </div>
        <Button
          className={classes.addNdcBtn}
          icon={<Plus size={16} style={{ marginRight: 4 }} />}
          label={selectedNDCs.size > 0 ? `Add ${selectedNDCs.size} NDC${selectedNDCs.size > 1 ? 's' : ''}` : 'Add NDC'}
          onClick={handleAddNDCButtonClick}
          loading={isAddingNDC}
        />
      </div>

      <div className={classes.tabsRow}>
        {TAB_CONFIG.map((tab) => {
          const isActive = activeTab === tab.key;
          const hasError = OOCStore.tabsWithMissingReasons.has(tab.key);
          return (
            <button
              key={tab.key}
              className={`${classes.tabButton} ${isActive ? classes.tabButtonActive : ''} ${hasError ? classes.tabButtonError : ''}`}
              onClick={() => OOCStore.setActiveTab(tab.key)}
            >
              {tab.label}
              <span className={`${classes.tabCount} ${isActive ? classes.tabCountActive : ''}`}>
                {tabCounts[tab.key]}
              </span>
              {hasError && <span className={classes.tabErrorDot} title="Some NDCs are missing a reason" />}
            </button>
          );
        })}
      </div>

      {activeTab !== 'ProvMAS' && (
        <>
          <FCColorLegend />

          <div className={classes.tableWrapper}>
            {activeTabAdjustments.length === 0 ? (
              <div className={classes.emptyState}>No NDCs in this tab. Use the search above to add NDCs.</div>
            ) : (
              <table className={classes.forecastTable}>
                <thead>
                  <tr>
                    <th className={classes.colNdc10}>NDC10</th>
                    <th className={classes.colNdc11}>NDC11</th>
                    <th className={classes.colSupplier}>Supplier</th>
                    <th className={classes.colMolecule}>Molecule</th>
                    <th className={classes.colConcept}>Concept</th>
                    <th className={classes.colPackOut}>Pack Out</th>
                    <th className={classes.colReason}>Reason</th>
                    {periods.map((p) => (
                      <th key={p} className={classes.colFc}>{periodToLabel(p)}</th>
                    ))}
                    <th className={classes.colNotes}>Notes</th>
                    <th className={classes.colRemove}></th>
                  </tr>
                </thead>
                <tbody>
                  {activeTabAdjustments.map((adj) => (
                    <AdjustmentRow key={adj.id} adjustment={adj} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ReferenceDataPanel adjustments={activeTabAdjustments} />
        </>
      )}

      {activeTab === 'ProvMAS' && activeTabAdjustments.length === 0 && (
        <div className={classes.emptyState}>No NDCs in this tab. Use the search above to add NDCs.</div>
      )}

      {activeTab === 'ProvMAS' && activeTabAdjustments.length > 0 && (
        <div className={classes.provCard}>
          <div className={classes.provCardHeader}>
            <div className={classes.provCardTitle}>Provisional Forecast Parameters</div>
            <span className={`${classes.provCardBadge} ${classes.provCardBadgeMas}`}>Prov. MAS</span>
          </div>
          <p className={classes.provCardDesc}>
            Configure facility percentages for each NDC. M1–M4 are editable; M5–M15 follow propagation rules. Select CMOP facilities to plan demand allocation.
          </p>
          {activeTabAdjustments.map((adj) => {
            const isProvMissingReason = OOCStore.missingReasonIds.includes(adj.id);
            return (
            <div key={adj.id} className={`${classes.provNdcBlock} ${isProvMissingReason ? classes.provNdcBlockError : ''}`}>
              <div className={classes.provNdcBlockHeader}>
                <span className={classes.provNdcBadge}>{adj.ndc11}</span>
                <span className={classes.provNdcMeta}>{adj.concept} · Pack {adj.packOut}</span>
                <button
                  className={classes.provNdcRemoveBtn}
                  onClick={() => OOCStore.removeAdjustment(adj.id)}
                  title="Remove NDC"
                >
                  <X size={14} />
                </button>
              </div>

              <div className={classes.provNdcDetails}>
                <div className={classes.provNdcAllFields}>
                  <div className={classes.provNdcInfoItem}>
                    <span className={classes.provNdcInfoLabel}>NDC10</span>
                    <span className={classes.provNdcInfoValue}>{adj.ndc10}</span>
                  </div>
                  <div className={classes.provNdcInfoItem}>
                    <span className={classes.provNdcInfoLabel}>Supplier</span>
                    <span className={classes.provNdcInfoValue}>{adj.supplier || '–'}</span>
                  </div>
                  <div className={classes.provNdcInfoItem}>
                    <span className={classes.provNdcInfoLabel}>Molecule</span>
                    <span className={classes.provNdcInfoValue}>{adj.molecule || '–'}</span>
                  </div>
                  <div className={classes.provNdcInfoItem}>
                    <span className={classes.provNdcInfoLabel}>Concept</span>
                    <span className={classes.provNdcInfoValue}>{adj.concept}</span>
                  </div>
                  <div className={classes.provNdcInfoItem}>
                    <span className={classes.provNdcInfoLabel}>Pack Out</span>
                    <span className={classes.provNdcInfoValue}>{adj.packOut}</span>
                  </div>
                  <div className={`${classes.provNdcInfoItem} ${isProvMissingReason ? classes.reasonCellError : ''}`}>
                    <span className={classes.provNdcInfoLabel}>Reason</span>
                    <Dropdown
                      className={`${classes.provReasonDropdown} ${isProvMissingReason ? 'p-invalid' : ''}`}
                      options={REASON_OPTIONS}
                      value={adj.reason ?? null}
                      onChange={(e) => OOCStore.updateAdjustmentReason(adj.id, e.value)}
                      placeholder="Select reason"
                      filter
                      filterPlaceholder="Search reason..."
                    />
                    {isProvMissingReason && <span className={classes.reasonErrorMsg}>Required</span>}
                  </div>
                  <div className={classes.provNdcInfoItem}>
                    <span className={classes.provNdcInfoLabel}>Notes</span>
                    <input
                      className={classes.provNotesInput}
                      type="text"
                      value={adj.notes}
                      onChange={(e) => OOCStore.updateNotes(adj.id, e.target.value)}
                      placeholder="Add notes..."
                    />
                  </div>
                </div>
              </div>

              <ProvMASNDCGrid adj={adj} periods={periods} />
            </div>
          ); })}
        </div>
      )}

      <div className={classes.actionBar}>
        <div className={classes.actionBarLeft}>
          <Button
            className={`p-button-text ${classes.cancelBtn}`}
            icon={<ArrowLeft size={16} style={{ marginRight: 4 }} />}
            label="Cancel"
            onClick={() => OOCStore.cancelAndSave()}
          />
        </div>
        <div className={classes.actionBarRight}>
          <Button
            className={`p-button-outlined ${classes.saveDraftBtn}`}
            icon={<Save size={16} style={{ marginRight: 4 }} />}
            label="Save Draft"
            onClick={() => OOCStore.saveDraft()}
            loading={isSaving}
          />
          <Button
            className={classes.communicateBtn}
            icon={<Send size={16} style={{ marginRight: 4 }} />}
            label="Communicate to SC"
            onClick={() => OOCStore.communicateToSC()}
            loading={isSaving}
            disabled={currentSubmission.adjustments.length === 0}
          />
        </div>
      </div>
    </div>
  );
};

export default observer(OOCCreateEdit);
